import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  Info,
  ChevronRight,
  ChevronLeft,
  Loader2
} from 'lucide-react';
import AssessmentLayout from '../../components/assessment/AssessmentLayout';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface Symptom {
  id: string;
  name_no: string;
  help_text?: string;
  value: number;
  order_index?: number;
}

interface Category {
  id: string;
  name_no: string;
  symptoms: Symptom[];
}

export default function AssessmentSymptoms() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [activeCategory, setActiveCategory] = useState(0);
  const [categories, setCategories] = useState<Category[]>([]);
  const [assessmentId, setAssessmentId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get assessment ID from sessionStorage
    const storedAssessmentId = sessionStorage.getItem('currentAssessmentId');
    if (!storedAssessmentId) {
      navigate('/assessment/start');
      return;
    }
    setAssessmentId(storedAssessmentId);

    // Get selected category IDs from sessionStorage
    const selectedCategoryIdsStr = sessionStorage.getItem('selectedCategoryIds');
    if (!selectedCategoryIdsStr) {
      navigate('/assessment/categories');
      return;
    }

    const selectedCategoryIds: string[] = JSON.parse(selectedCategoryIdsStr);

    // Fetch symptom groups with their symptoms from Supabase
    const fetchData = async () => {
      try {
        const { data: groups, error: groupsError } = await supabase
          .from('symptom_groups')
          .select('id, name_no')
          .in('id', selectedCategoryIds)
          .eq('status', 'published')
          .order('order_index', { ascending: true });

        if (groupsError) throw groupsError;
        if (!groups || groups.length === 0) {
          navigate('/assessment/categories');
          return;
        }

        // Fetch symptoms for selected groups
        const { data: symptoms, error: symptomsError } = await supabase
          .from('symptoms')
          .select('id, name_no, help_text, order_index, group_id')
          .in('group_id', selectedCategoryIds)
          .eq('status', 'published')
          .order('order_index', { ascending: true });

        if (symptomsError) throw symptomsError;

        // Transform to local state format with value field
        const transformed: Category[] = groups.map((group: any) => ({
          id: group.id,
          name_no: group.name_no,
          symptoms: (symptoms || [])
            .filter((s: any) => s.group_id === group.id)
            .map((symptom: any) => ({
              id: symptom.id,
              name_no: symptom.name_no,
              help_text: symptom.help_text,
              value: 0,
              order_index: symptom.order_index,
            })),
        }));

        setCategories(transformed);
      } catch (error) {
        console.error('Error fetching symptom data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleSymptomChange = async (categoryId: string, symptomId: string, value: number) => {
    // Update local state immediately for responsiveness
    setCategories(cats =>
      cats.map(cat =>
        cat.id === categoryId
          ? {
              ...cat,
              symptoms: cat.symptoms.map(sym =>
                sym.id === symptomId ? { ...sym, value } : sym
              )
            }
          : cat
      )
    );

    // Update database if assessment ID exists
    if (assessmentId && value > 0 && profile) {
      try {
        await supabase
          .from('assessment_symptoms')
          .upsert({
            assessment_id: assessmentId,
            symptom_id: symptomId,
            intensity: value,
          }, {
            onConflict: 'assessment_id,symptom_id',
          });
      } catch (error) {
        console.error('Error updating symptom intensity:', error);
      }
    }
  };

  const handleBack = () => {
    navigate('/assessment/categories');
  };

  const handleNext = () => {
    if (activeCategory < categories.length - 1) {
      setActiveCategory(activeCategory + 1);
    } else {
      // All symptoms saved to database already via handleSymptomChange
      // Just navigate to module recommendation
      navigate('/assessment/module');
    }
  };

  const handlePrevious = () => {
    if (activeCategory > 0) {
      setActiveCategory(activeCategory - 1);
    }
  };

  if (loading || categories.length === 0) {
    return (
      <AssessmentLayout
        title="Modulbasert tilnærming for betjenter"
        subtitle="Laster symptomer..."
        backTo="/assessment/categories"
        currentStep={4}
      >
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-400" />
        </div>
      </AssessmentLayout>
    );
  }

  const currentCategory = categories[activeCategory];
  const totalSymptoms = currentCategory.symptoms.length;
  const selectedSymptoms = currentCategory.symptoms.filter(s => s.value > 0).length;

  return (
    <AssessmentLayout
      title="Modulbasert tilnærming for betjenter"
      subtitle="Velg symptomer som passer til innsatte akkurat nå"
      backTo="/assessment/categories"
      currentStep={4}
    >
      {/* Category header with stats */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{currentCategory.name_no}</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Velg og juster alvorlighetsgrad for observerte symptomer
            </p>
          </div>
          <div className="hidden lg:block text-center">
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{selectedSymptoms}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">av {totalSymptoms} valgt</div>
          </div>
        </div>
      </div>

      {/* Symptoms Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {currentCategory.symptoms.map((symptom, index) => (
          <div
            key={symptom.id}
            className={`
              bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6 transition-all
              ${symptom.value > 0 ? 'border-2 border-blue-600 dark:border-blue-500' : 'border-2 border-gray-200 dark:border-gray-700'}
            `}
          >
            {/* Symptom Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg font-bold text-gray-900 dark:text-white">
                    {index + 1}. {symptom.name_no}
                  </span>
                </div>
                {symptom.help_text && (
                  <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg mt-2">
                    <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <span className="text-xs text-blue-700 dark:text-blue-300">{symptom.help_text}</span>
                  </div>
                )}
              </div>
              <input
                type="checkbox"
                checked={symptom.value > 0}
                onChange={(e) =>
                  handleSymptomChange(
                    currentCategory.id,
                    symptom.id,
                    e.target.checked ? 100 : 0
                  )
                }
                className="w-6 h-6 rounded focus:ring-2 focus:ring-blue-500 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 checked:bg-blue-600 dark:checked:bg-blue-500 checked:border-blue-600 dark:checked:border-blue-500"
              />
            </div>

            {/* Slider */}
            <div className="space-y-3">
              <div className="relative">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={symptom.value}
                  onChange={(e) =>
                    handleSymptomChange(
                      currentCategory.id,
                      symptom.id,
                      parseInt(e.target.value)
                    )
                  }
                  className="w-full h-3 rounded-full appearance-none cursor-pointer symptom-slider"
                  data-value={symptom.value}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-gray-900 dark:text-white">
                  {symptom.value}%
                </span>
                {symptom.value > 0 && (
                  <span className={`
                    px-3 py-1 rounded-full text-xs font-semibold
                    ${symptom.value >= 75 ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                      symptom.value >= 50 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' :
                      symptom.value >= 25 ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' :
                      'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}
                  `}>
                    {symptom.value >= 75 ? 'Høy' :
                     symptom.value >= 50 ? 'Moderat' :
                     symptom.value >= 25 ? 'Lav' :
                     'Minimal'}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Footer */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
        <div className="flex items-center justify-between">
          {/* Previous button */}
          <button
            onClick={handlePrevious}
            disabled={activeCategory === 0}
            className={`
              flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all
              ${activeCategory > 0
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                : 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'}
            `}
          >
            <ChevronLeft className="h-5 w-5" />
            Forrige kategori
          </button>

          {/* Category indicators */}
          <div className="hidden md:flex items-center gap-2">
            {categories.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveCategory(index)}
                className={`
                  w-3 h-3 rounded-full transition-all
                  ${index === activeCategory
                    ? 'w-8 bg-blue-600 dark:bg-blue-500'
                    : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'}
                `}
              />
            ))}
          </div>

          {/* Next/Complete button */}
          <button
            onClick={handleNext}
            className="flex items-center gap-2 px-8 py-3 bg-green-600 dark:bg-green-500 text-white rounded-lg font-semibold hover:bg-green-700 dark:hover:bg-green-600 transition-colors"
          >
            {activeCategory < categories.length - 1 ? (
              <>
                Neste kategori
                <ChevronRight className="h-5 w-5" />
              </>
            ) : (
              <>
                Fullfør vurdering
                <Check className="h-5 w-5" />
              </>
            )}
          </button>
        </div>
      </div>
    </AssessmentLayout>
  );
}
